import { createContext, FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  IstatCapabilities,
  IstatProcedureCode,
  MeteringModule,
  useContextPropertiesQuery,
  useContextTenantsQuery,
  useContextUserPermissionsQuery,
  useContextCashDocumentActivesQuery,
  useGetBuyNowUrlFromStoreQuery,
  useGetIstatSystemCapabilitiesQuery,
  useUnusedPlantsQuery,
  FiscalType,
  useAppDailyClosureEventSubscription,
  DailyClosureProcedure,
} from '../../../api/api.generated'
import { useAuth } from '../auth/auth.hook'
import { ContextProperty, ContextTenant, HiCContext, HiCContextState } from './context.types'
import useStateWithCallback from '../use-state-with-callback/use-state-with-callback'
import { ServerError } from '@apollo/client'

export const hicContext = createContext<HiCContext>({} as HiCContext)

export const ContextProvider: FC = ({ children }) => {
  const context = useProvideHiC()
  return <hicContext.Provider value={context}>{children}</hicContext.Provider>
}

const useProvideHiC = (): HiCContext => {
  const { state: context, setState: setContext } = useStateWithCallback<HiCContextState>({ loading: true }, null)
  const [selectedProperty, setSelectedProperty] = useState<{ tenant: ContextTenant; property: ContextProperty | null; onChange?: () => void }>()

  useEffect(() => {
    if (!context.value.loading) {
      localStorage.setItem('hic-tenant', context.value.currentTenant.id)
      if (context.value.currentProperty?.id) {
        localStorage.setItem('hic-property', context.value.currentProperty.id)
      }
    }
  }, [context])

  const { auth } = useAuth()

  const [retryCount, setRetryCount] = useState(0)
  const tenantsQuery = useContextTenantsQuery({
    skip: !auth.isAuthenticated,
    notifyOnNetworkStatusChange: true,
    onError(error) {
      if ((error.networkError as ServerError).result && retryCount === 0) {
        console.log('Retry...')
        setTimeout(() => {
          tenantsQuery.refetch()
          setRetryCount(1)
        }, 1000)
      }
    },
  })

  const tenants = useMemo(() => {
    const tenantsLoaded = tenantsQuery.data?.tenants ?? []
    return tenantsLoaded //
    //.filter((t) => t.backoffice) //FIXME: Aggiunto per testare il caso del tenant di backoffice
  }, [tenantsQuery.data])

  const tenantsIds = useMemo(() => {
    return tenantsQuery.data?.tenants
      ? tenantsQuery.data.tenants //
          //.filter((t) => t.backoffice) //FIXME: Aggiunto per testare il caso del tenant di backoffice
          .map((t) => t.id)
      : undefined
  }, [tenantsQuery.data])

  const propertiesQuery = useContextPropertiesQuery({
    variables: { tenantId: tenantsIds ?? [] },
    skip: !auth.isAuthenticated || !tenantsIds,
  })
  const properties = useMemo(() => {
    const propertiesLoaded = propertiesQuery.data?.properties ?? []
    return propertiesLoaded
  }, [propertiesQuery.data])

  const getUserPermissionsQuery = useContextUserPermissionsQuery({
    skip: true,
  })

  const getUnusedPlantsQuery = useUnusedPlantsQuery({
    skip: true,
  })

  const getIstatSystemCapabilities = useGetIstatSystemCapabilitiesQuery({ skip: true })

  const getCashDocumentActives = useContextCashDocumentActivesQuery({ skip: true })

  useEffect(() => {
    const updateTenantAndProperty = async () => {
      if (tenants && propertiesQuery.data?.properties) {
        // Checking propertiesQuery.data ensure that properties are loaded yet
        let currentTenant: ContextTenant | undefined
        let currentProperty: ContextProperty | undefined | null = null
        const storageTenant = localStorage.getItem('hic-tenant')
        if (storageTenant) {
          currentTenant = tenants.find((t) => t.id === storageTenant)
        }

        if (!currentTenant && tenants.length > 0) {
          currentTenant = tenants[0]
        }

        if (properties.length > 0) {
          const storageProperty = localStorage.getItem('hic-property')
          if (storageProperty) {
            currentProperty = properties.find((p) => p.id === storageProperty && p.tenantId === currentTenant?.id)
          }

          // Try to set first property of current tenant
          if (!currentProperty) {
            currentProperty = properties.filter((p) => p.tenantId === currentTenant?.id)[0]
          }

          // If was not possible to use first property of current tenant, use the first property available and change current tenant.
          if (!currentProperty && !selectedProperty) {
            currentProperty = properties[0]
            currentTenant = tenants.find((t) => t.id === properties[0].tenantId)
          }
        }

        if (currentTenant) {
          const updPermissions = await getUserPermissionsQuery.refetch({
            input: {
              tenantId: currentTenant.id,
              propertyId: currentProperty?.id,
            },
          })
          console.log('Permissions: ', updPermissions)

          const getTenantsUnusedPlantsResult = tenants.map(async (t) => {
            const unusedPlants = await getUnusedPlantsQuery.refetch({
              tenantId: t.id,
            })
            return { tenantId: t.id, unusedPlants: unusedPlants.data.unusedPlants }
          })

          const tenantUnusedPlants = await Promise.all(getTenantsUnusedPlantsResult)

          const cityTaxIsConfigured =
            currentProperty?.cityTaxConfiguration &&
            currentProperty?.cityTaxConfiguration?.chargeCategoryId &&
            currentProperty?.cityTaxConfiguration?.configurations?.find((configuration) => configuration)?.amount &&
            currentProperty?.cityTaxConfiguration?.configurations?.find((configuration) => configuration)?.rule
              ? true
              : false

          const istatIsConfigured =
            currentProperty?.istatConfigurations && currentProperty?.istatConfigurations?.find((configuration) => configuration.name && configuration.procedureCode) ? true : false

          // Store capabilities of current property Istat configurations
          const currentPropertyIstatCapabilities: Map<IstatProcedureCode, IstatCapabilities> = new Map<IstatProcedureCode, IstatCapabilities>()
          const currentPropertyDistinctIstatProcedure = currentProperty?.istatConfigurations?.reduce((accumulated: IstatProcedureCode[], current) => {
            if (accumulated.includes(current.procedureCode)) return accumulated
            return [...accumulated, current.procedureCode]
          }, [])
          if (currentPropertyDistinctIstatProcedure) {
            const getIstatSystemCapabilitiesResult = currentPropertyDistinctIstatProcedure?.map((procedureCode) => getIstatSystemCapabilities.refetch({ procedureCode }))
            const istatProcedureCodesSystemCapabilities = await Promise.all(getIstatSystemCapabilitiesResult)

            istatProcedureCodesSystemCapabilities.forEach((capabilities, index) => {
              const procedureCode = currentPropertyDistinctIstatProcedure[index]
              if (procedureCode && !capabilities.error && capabilities.data.getIstatSystemCapabilities)
                currentPropertyIstatCapabilities.set(procedureCode, capabilities.data.getIstatSystemCapabilities)
            })
          }

          const psIsConfigured = currentProperty?.psConfigurations && currentProperty?.psConfigurations?.find((configuration) => configuration.name) ? true : false

          const isTrial = Boolean(updPermissions.data.getUserPermissions.trialModules?.find((m) => m.module === MeteringModule.PMS))

          let receiptIsActive = true

          if (currentProperty?.id) {
            const cashDocumentActives = await getCashDocumentActives.refetch({
              filter: {
                tenantId: { eq: currentTenant.id },
                propertyId: { eq: currentProperty?.id },
              },
            })

            receiptIsActive = cashDocumentActives.data.cashDeskTypes?.some((c) => c.documentsActive.find((i) => i.fiscalType === FiscalType.RECEIPT)) ?? false
          }

          setContext({
            value: {
              loading: false,
              tenants: tenants,
              properties: properties,
              currentTenant,
              permissions: {
                userModules: updPermissions.data.getUserPermissions.userModules,
                modules: updPermissions.data.getUserPermissions.modules,
                features: updPermissions.data.getUserPermissions.features,
                userRole: updPermissions.data.getUserPermissions.userRole,
                trialModules: updPermissions.data.getUserPermissions.trialModules,
              },
              currentProperty: currentProperty ? currentProperty : null,
              settings: {
                accounting: {
                  coDocumentType: receiptIsActive ? 'RECEIPT' : 'INVOICE',
                  depositDocumentType: 'NON_FISCAL',
                },
              },
              tenantUnusedPlants: tenantUnusedPlants.map((tup) => ({ ...tup, unusedPlants: [...(tup.unusedPlants.filter((up) => up.id) ?? [])] })),
              cityTaxIsConfigured: cityTaxIsConfigured,
              istatIsConfigured: istatIsConfigured,
              psIsConfigured: psIsConfigured,
              onboardingTenantId: null,
              currentPropertyIstatCapabilities,
              isTrial,
              tenantSellerName: updPermissions.data.getUserPermissions.tenantSellerName ?? null,
            },
            onChange: selectedProperty?.onChange ?? null,
          })
        }
      }
    }

    updateTenantAndProperty()
  }, [tenants, properties, selectedProperty])

  const setCurrent = useCallback(
    async (tenant: ContextTenant, property: ContextProperty | null, onChange?: () => void) => {
      if (context.value.loading) return
      if (!tenants.some((t) => t.id === tenant.id)) return
      const currentTenant = tenant
      const tenantProperties = properties.filter((p) => p.tenantId === currentTenant.id)
      let currentProperty: ContextProperty | null = null
      if (property === null || !tenantProperties.some((p) => p.id === property.id)) {
        currentProperty = tenantProperties[0] ?? null
      } else {
        currentProperty = property
      }
      if (currentProperty?.id) localStorage.setItem('hic-property', currentProperty?.id)
      localStorage.setItem('hic-tenant', currentTenant.id)
      setSelectedProperty({ tenant: currentTenant, property: currentProperty, onChange: onChange })
    },
    [context, tenants, properties],
  )

  const onboardingSetParams = useCallback(
    async (onboardingTenantId: string | null, onUpdateContextCallback: () => void) => {
      const updContext = { ...context, value: { ...context.value, onboardingTenantId: onboardingTenantId }, onChange: onUpdateContextCallback }
      setContext(updContext)
    },
    [context],
  )

  const onboardingCompleted = async (propertyId: string | null, onSuccess: () => void, onFail: () => void) => {
    const refetchProperties = await propertiesQuery.refetch()
    if (refetchProperties && refetchProperties.data.properties) {
      const newPropertyContext = refetchProperties.data?.properties ? refetchProperties.data.properties.find((p) => p.id === propertyId) : undefined
      if (newPropertyContext) {
        if (!context.value.loading) {
          localStorage.setItem('hic-property', newPropertyContext.id)
          localStorage.setItem('hic-tenant', newPropertyContext.tenantId)
          setSelectedProperty({ tenant: context.value.tenants.find((t) => t.id === newPropertyContext.tenantId) ?? context.value.currentTenant, property: newPropertyContext, onChange: onSuccess })
          return
        }
      }
    }
    onFail()
  }

  const refetchTenants = useCallback(() => {
    tenantsQuery.refetch()
  }, [tenantsQuery])

  const [isOpenBuyDrawer, setIsOpenBuyDrawer] = useState(false)

  const openBuyDrawer = useCallback(() => {
    setIsOpenBuyDrawer(true)
  }, [setIsOpenBuyDrawer])

  const closeBuyDrawer = useCallback(() => {
    setIsOpenBuyDrawer(false)
  }, [setIsOpenBuyDrawer])

  const queryGetUrl = useGetBuyNowUrlFromStoreQuery({ skip: true })

  const getBuyNowUrl = useCallback(
    async (userEmail: string, planReference: string, subscriptionIdToUpgrade: string) => {
      const updPermissions = await queryGetUrl.refetch({
        request: {
          productCode: 'ts-hospitality',
          action: 'TrialFromApplication',
          email: userEmail,
          planReference,
          subscriptionIdToUpgrade,
        },
      })
      if (updPermissions.data.getUrlFromStore.url) {
        return updPermissions.data.getUrlFromStore.url
      }
    },
    [queryGetUrl],
  )

  /** Subscribes to ChageDate success events.*/
  useAppDailyClosureEventSubscription({
    variables: {
      filter: {
        tenantId: !context.value.loading && context.value.currentProperty?.tenantId ? context.value.currentProperty?.tenantId : '',
        propertyId: !context.value.loading && context.value.currentProperty?.id ? context.value.currentProperty?.id : '',
        procedureName: DailyClosureProcedure.ChangeDate,
        result: true,
      },
    },
    skip: Boolean(context.value.loading || !context.value.currentProperty),
    onSubscriptionData: (data) => {
      //Reloads properties when an event happens
      if (!data.subscriptionData.error) {
        propertiesQuery.refetch()
      }
    },
  })

  return {
    setCurrent,
    onboardingSetParams,
    onboardingCompleted,
    refetchTenants,
    context: context.value,
    loadingTenants: tenantsQuery.loading,
    getBuyNowUrl,
    closeBuyDrawer,
    openBuyDrawer,
    isOpenBuyDrawer,
  }
}
